import { defaultAxiosInstance } from '../config/axiosConfig';

// Build the shared pagination/search query params (omitting empties).
const listParams = ({ search, limit, offset } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  return params;
};

/**
 * Company Admin console service (spec §5).
 *
 * Auth: the company admin's own x-api-key (defaultAxiosInstance). Requires the
 * user to hold user_management (user screens) / role_management (role screens);
 * company_admin holds both. Every call is automatically scoped to the caller's
 * own company on the backend — there is no company parameter anywhere here.
 *
 * All methods return `response.data`; callers normalize shape.
 */
class CompanyAdminService {
  // ── Users (§5.1) ─────────────────────────────────────────────────────────
  /** → { users, total, limit, offset } (searches email + first/last name) */
  async listUsers(opts = {}) {
    const res = await defaultAxiosInstance.get('/company-admin/users', { params: listParams(opts) });
    return res.data;
  }

  /** Body: { email, first_name, last_name?, role_ids?: [] } (company_id ignored). */
  async createUser(payload) {
    const res = await defaultAxiosInstance.post('/company-admin/users', payload);
    return res.data;
  }

  /** Body: { is_active: true|false } only. → AdminUser */
  async updateUser(userId, payload) {
    const res = await defaultAxiosInstance.patch(`/company-admin/users/${userId}`, payload);
    return res.data;
  }

  async assignUserRole(userId, roleId) {
    const res = await defaultAxiosInstance.post(`/company-admin/users/${userId}/roles`, {
      role_id: roleId,
    });
    return res.data;
  }

  async removeUserRole(userId, roleId) {
    const res = await defaultAxiosInstance.delete(`/company-admin/users/${userId}/roles/${roleId}`);
    return res.data;
  }

  /** → { user_id, api_key: 'NEW-KEY-SHOWN-ONCE' } */
  async resetUserApiKey(userId) {
    const res = await defaultAxiosInstance.post(`/company-admin/users/${userId}/reset-api-key`);
    return res.data;
  }

  // ── Roles (§5.2) ─────────────────────────────────────────────────────────
  /** → { roles, total, ... } or Role[] (searches role name + description) */
  async listRoles(opts = {}) {
    const res = await defaultAxiosInstance.get('/company-admin/roles', { params: listParams(opts) });
    return res.data;
  }

  /** Body: { name, description, permission_keys } */
  async createRole(payload) {
    const res = await defaultAxiosInstance.post('/company-admin/roles', payload);
    return res.data;
  }

  /** Body: { name?, description?, permission_keys? } */
  async updateRole(roleId, payload) {
    const res = await defaultAxiosInstance.put(`/company-admin/roles/${roleId}`, payload);
    return res.data;
  }

  /** → { deleted: true, affected_users: N } */
  async deleteRole(roleId) {
    const res = await defaultAxiosInstance.delete(`/company-admin/roles/${roleId}`);
    return res.data;
  }

  // ── Permission catalog + entitlements (§5.2 / §5.3) ──────────────────────
  /**
   * Grantable permission catalog, already filtered to the company's envelope —
   * exactly what this admin may grant. → { permissions: [...] } or bare array.
   */
  async getPermissions() {
    const res = await defaultAxiosInstance.get('/company-admin/permissions');
    return res.data;
  }

  /** Read-only view of the company's envelope. → { company_id, permission_keys } */
  async getEntitlements() {
    const res = await defaultAxiosInstance.get('/company-admin/entitlements');
    return res.data;
  }

  // ── LLM Keys (company-level, spec: UI implementation handoff) ────────────
  /** → { credentials: [...] } or bare array of CompanyCredential, scoped to caller's company. */
  async getLLMKeys() {
    const res = await defaultAxiosInstance.get('/company-admin/llm-keys');
    return res.data;
  }

  /** Body: { api_key, base_url? } only — never allow_platform_fallback (platform-only field). → CompanyCredential */
  async setLLMKey(providerId, payload) {
    const res = await defaultAxiosInstance.put(`/company-admin/llm-keys/${providerId}`, payload);
    return res.data;
  }

  /** → updated CompanyCredential (allow_platform_fallback preserved). */
  async deleteLLMKey(providerId) {
    const res = await defaultAxiosInstance.delete(`/company-admin/llm-keys/${providerId}`);
    return res.data;
  }
}

const companyAdminService = new CompanyAdminService();
export default companyAdminService;
